import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { SalesTaxConfig, SalesTaxExemptionType } from '../../service-data/sales-tax-exemption.service';
import {
  buildStateMatrixCells,
  selectedCodes,
  StateMatrixCell,
  StateMatrixCellKind,
} from '../../util/sales-tax-exemption-states';

// Re-exported so importers of this component keep working; the declarations
// themselves live beside the logic that produces them.
export { StateMatrixCell, StateMatrixCellKind } from '../../util/sales-tax-exemption-states';

/**
 * Shared component that renders the full US Sales-Tax-Exemption
 * state set as a grid of checkboxes with reciprocity / nexus visual
 * cues. Used by:
 *
 * - Organization-side exemption request form (writable)
 * - SP/Network review screen (writable, reviewer overrides accepted set)
 * - SP/Network nexus & reciprocity editor (writable, no `issuingState`)
 *
 * Inputs:
 * - `config` — the SP's salesTaxConfig (nexus + reciprocity lists).
 * - `type`   — `wholesale` | `other` | `non-exempt` (selects which reciprocity list to use).
 * - `issuingState` — 2-letter code of the cert-issuing state, or `''`.
 *                    When non-empty, the matrix auto-checks that state and
 *                    every reciprocal state for the chosen `type`.
 * - `selectedStates` — current selection set (two-way bound via
 *                      `selectedStatesChange`).
 * - `readOnly` — disables interaction (review queue read mode).
 * - `mode` — `exemption` (default; uses nexus+reciprocity rules) or
 *            `nexus` (cells are always plain — used by the SP nexus editor
 *            where reciprocity isn't relevant) or `reciprocity` (cells are
 *            plain too — used by the SP reciprocity editor).
 *
 * Cell classification and the default selection live in
 * `src/app/util/sales-tax-exemption-states.ts` so they can be exercised by
 * `npm run smoke:sales-tax-states`. The rule worth knowing here: the claim a
 * certificate supports is `{issuingState} ∪ reciprocity[type]`, and neither
 * half is filtered by the SP's nexus. The component re-seeds that claim
 * whenever `type`, `issuingState`, or the first arrival of `config` changes
 * the basis — not only when the selection is empty.
 *
 * The component is fully presentational; persistence is the caller's
 * responsibility.
 */
@Component({
  selector: 'app-sales-tax-states-matrix',
  templateUrl: './sales-tax-states-matrix.component.html',
  styleUrls: ['./sales-tax-states-matrix.component.css']
})
export class SalesTaxStatesMatrixComponent implements OnChanges, OnInit {
  @Input() config: SalesTaxConfig | null = null;
  @Input() type: SalesTaxExemptionType = 'non-exempt';
  @Input() issuingState = '';
  @Input() selectedStates: string[] = [];
  @Input() readOnly = false;
  @Input() mode: 'exemption' | 'nexus' | 'reciprocity' = 'exemption';

  @Output() selectedStatesChange = new EventEmitter<string[]>();

  cells: StateMatrixCell[] = [];

  ngOnInit() {
    // Guarantee the grid is populated on first mount, even when no bound
    // input reference differs from the declared default (Angular skips
    // ngOnChanges in that case, which left the grid empty in practice).
    if (this.cells.length === 0) {
      this.rebuild();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // The default claim is derived from (type, issuingState, config). When any
    // of those change the previously seeded set is stale and must be rebuilt —
    // switching wholesale -> other otherwise left the wholesale reciprocity set
    // in place. A simultaneous `selectedStates` change means the PARENT loaded a
    // different record (reviewer opening another queue item), so adopt its
    // selection instead of overwriting it.
    const selectionChanged = !!changes.selectedStates;
    const basisChanged =
      (!!changes.type && !changes.type.firstChange) ||
      (!!changes.issuingState && !changes.issuingState.firstChange) ||
      // The SP config loads asynchronously; until it arrives reciprocity is
      // unknown and any seed built from it was empty.
      (!!changes.config && !changes.config.firstChange
        && !changes.config.previousValue && !!changes.config.currentValue);
    this.rebuild(basisChanged && !selectionChanged);
  }

  /** True when the user has selected at least one non-reciprocal state. */
  get hasWarning(): boolean {
    return this.cells.some(c => c.warned);
  }

  /** List of state codes that currently warrant a warning. */
  get warningCodes(): string[] {
    return this.cells.filter(c => c.warned).map(c => c.code);
  }

  toggle(cell: StateMatrixCell): void {
    if (this.readOnly) { return; }
    if (cell.kind === 'no-nexus' && this.mode === 'exemption') {
      // Allow the user to express an exemption claim for a no-nexus state,
      // but per UX the cell stays disabled. A no-op click is fine.
      return;
    }
    if (cell.kind === 'issuing' && this.mode === 'exemption' && this.issuingState) {
      // Issuing state is locked-on while a cert is on file.
      return;
    }
    cell.selected = !cell.selected;
    cell.warned = cell.selected && cell.kind === 'non-reciprocal';
    this.emit();
  }

  // ── private ────────────────────────────────────────────────────────

  private rebuild(reseedDefaults: boolean = false) {
    const incoming = (this.selectedStates || [])
      .map(c => String(c || '').toUpperCase()).sort().join(',');

    this.cells = buildStateMatrixCells({
      mode: this.mode,
      config: this.config,
      type: this.type,
      issuingState: this.issuingState,
      selectedStates: this.selectedStates,
      reseedDefaults,
    });

    // If we synthesized a default selection, emit it so the parent's
    // ngModel-style binding picks it up.
    const synthesized = selectedCodes(this.cells);
    if (incoming !== synthesized.slice().sort().join(',')) {
      // Defer to avoid mutating an Input during change detection.
      setTimeout(() => this.selectedStatesChange.emit(synthesized), 0);
    }
  }

  private emit() {
    this.selectedStatesChange.emit(selectedCodes(this.cells));
  }
}


